import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useLocation } from "react-router-dom";

// MUI Components
import {
  AppBar,
  Box,
  Toolbar,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Chip,
  Button,
  Avatar,
  Tooltip,
  Badge,
  alpha,
  Popover,
  Checkbox,
  Slider,
  ListItemSecondaryAction,
} from "@mui/material";

// MUI Icons
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import HomeIcon from "@mui/icons-material/Home";
import BookIcon from "@mui/icons-material/Book";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LayersIcon from "@mui/icons-material/Layers";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import WavesIcon from "@mui/icons-material/Waves";
import TerrainIcon from "@mui/icons-material/Terrain";
import DownloadIcon from "@mui/icons-material/Download";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import OpacityIcon from "@mui/icons-material/Opacity";

// Site Settings
import { retrievePublicSiteSettings } from "src/redux/actions/siteSetting";
import environment from "src/config/environment";

// Context untuk map state
import { useMapState } from "src/contexts/MapStateContext";

// Login Modal
import LoginModal from "src/components/LoginModal";

// Menu items configuration dengan kategori
const menuCategories = [
  {
    title: "Navigasi Utama",
    items: [
      { text: "Beranda", icon: <HomeIcon />, path: "/", color: "#1976d2" },
      { text: "Katalog", icon: <BookIcon />, path: "/katalog", color: "#2e7d32" },
      { text: "Berita", icon: <NewspaperIcon />, path: "/berita", color: "#ed6c02" },
    ]
  },
  {
    title: "Unduh Data",
    items: [
      { text: "Unduh BATNAS", icon: <WavesIcon />, path: "/unduh-batnas", color: "#0288d1" },
      { text: "Unduh DEMNAS", icon: <TerrainIcon />, path: "/unduh-demnas", color: "#388e3c" },
    ]
  },
  {
    title: "Bantuan",
    items: [
      { text: "Panduan Pengguna", icon: <HelpOutlineIcon />, path: "/panduan", color: "#7b1fa2" },
    ]
  }
];

function Header() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [layerMenuOpen, setLayerMenuOpen] = useState(false);
  const [layerMenuAnchorEl, setLayerMenuAnchorEl] = useState(null);
  const [loginMenuAnchorEl, setLoginMenuAnchorEl] = useState(null);
  const loginMenuOpen = Boolean(loginMenuAnchorEl);
  const siteSetting = useSelector((state) => state.siteSetting);
  const { user, isLoggedIn } = useSelector((state) => state.auth);

  // Map state dari context
  const { mapLayer, deleteDataset, handleVisible, openCatalog, setZoomToMap, handleOpacity } = useMapState();

  // Check current page type
  const isMapPage = location.pathname === "/peta" || location.pathname === "/map" || location.pathname === "/";
  const isLoginPage = location.pathname === "/auth/login";

  useEffect(() => {
    dispatch(retrievePublicSiteSettings());
  }, [dispatch]);

  // Drawer handlers
  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  // Layer menu handlers
  const handleLayerMenuClick = (event) => {
    setLayerMenuAnchorEl(event.currentTarget);
    setLayerMenuOpen(true);
  };

  const handleLayerMenuClose = () => {
    setLayerMenuAnchorEl(null);
    setLayerMenuOpen(false);
  };

  // layer delete with event notification
  const handleDeleteLayer = (layerId) => {
    deleteDataset(layerId);

    // Emit custom event for map to handle layer deletion
    const deleteEvent = new CustomEvent('layerDeleted', {
      detail: { layerId }
    });
    window.dispatchEvent(deleteEvent);

    handleLayerMenuClose();
  };

  // layer visibility toggle
  const handleToggleLayerVisibility = (layerId) => {
    handleVisible(layerId);

    // Emit custom event for map to handle visibility toggle
    const visibilityEvent = new CustomEvent('layerVisibilityToggled', {
      detail: { layerId }
    });
    window.dispatchEvent(visibilityEvent);
  };

  // Zoom to layer - use context function
  const handleZoomToLayer = (layerId) => {
    setZoomToMap(layerId);
    handleLayerMenuClose();
  };

  // Handle layer opacity change - use context function
  const handleLayerOpacity = (layerId, opacity) => {
    handleOpacity(layerId, opacity);
  };

  // Drawer content dengan kategori dan animasi
  const drawerContent = (
    <Box
      sx={{
        width: 320,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f8f9fa'
      }}
      role="presentation"
    >
      {/* Drawer Header */}
      <Box sx={{
        bgcolor: 'primary.main',
        color: 'white',
        p: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={`${environment.api}site-settings/logo`}
            alt={`logo ${siteSetting?.name || "SIBATNAS"}`}
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              border: '2px solid white'
            }}
          />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: 'white' }}>
              {siteSetting?.name || "SIBATNAS"}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 1, fontSize: '0.7rem', color: 'rgba(255,255,255,0.9)' }}>
              Sistem Informasi Batimetri
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={toggleDrawer(false)}
          sx={{
            color: 'white',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Menu Categories */}
      <Box sx={{
        flexGrow: 1,
        overflowY: 'auto',
        py: 2,
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#555',
        },
      }}>
        {menuCategories.map((category, categoryIndex) => (
          <Box key={categoryIndex} sx={{ mb: 3 }}>
            {/* Category Title */}
            <Typography
              variant="overline"
              sx={{
                px: 3,
                mb: 1,
                display: 'block',
                color: 'text.secondary',
                fontWeight: 700,
                fontSize: '0.7rem',
                letterSpacing: '0.5px'
              }}
            >
              {category.title}
            </Typography>

            {/* Category Items */}
            <List sx={{ px: 2 }}>
              {category.items.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <ListItem
                    key={item.text}
                    disablePadding
                    sx={{ mb: 0.5 }}
                    onClick={toggleDrawer(false)}
                  >
                    <ListItemButton
                      component={RouterLink}
                      to={item.path}
                      selected={isActive}
                      sx={{
                        borderRadius: 2,
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          bgcolor: item.color,
                          transform: isActive ? 'scaleY(1)' : 'scaleY(0)',
                          transition: 'transform 0.2s ease',
                        },
                        '&:hover': {
                          bgcolor: alpha(item.color, 0.08),
                          transform: 'translateX(4px)',
                          '&::before': {
                            transform: 'scaleY(1)',
                          }
                        },
                        '&.Mui-selected': {
                          bgcolor: item.color,
                          color: 'white',
                          '&:hover': {
                            bgcolor: item.color,
                            filter: 'brightness(1.1)',
                          }
                        }
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 40,
                          color: isActive ? 'white' : 'text.secondary',
                          transition: 'color 0.2s ease'
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          sx: {
                            fontWeight: isActive ? 600 : 500,
                            fontSize: '0.95rem',
                            color: isActive ? 'white' : 'text.primary'
                          }
                        }}
                      />
                      {isActive && (
                        <ChevronRightIcon
                          sx={{
                            color: 'white',
                            fontSize: '1.2rem'
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Drawer Footer */}
      <Box sx={{
        p: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'white'
      }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', textAlign: 'center' }}>
          © 2025 {siteSetting?.name || "SIBATNAS"}
        </Typography>
      </Box>
    </Box>
  );

  // ========== LOGIN PAGE HEADER ==========
  if (isLoginPage) {
    return (
      <>
        <AppBar
          position="static"
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters sx={{ py: 1 }}>
              {/* Logo dan Judul */}
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                  color: "primary.main",
                  flexGrow: 1,
                }}
              >
                <Avatar
                  src={`${environment.api}site-settings/logo`}
                  alt={`logo ${siteSetting?.name || "SIBATNAS"}`}
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    bgcolor: 'primary.light'
                  }}
                />
                <Box>
                  <Typography
                    variant="h6"
                    component="h1"
                    sx={{
                      fontFamily: "finalsix, sans-serif",
                      fontWeight: 700,
                    }}
                  >
                    {siteSetting?.name || "SIBATNAS"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.75rem'
                    }}
                  >
                    Sistem Informasi Batimetri Nasional
                  </Typography>
                </Box>
              </Box>

              {/* Link kembali ke beranda */}
              <IconButton
                component={RouterLink}
                to="/"
                sx={{
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText'
                  }
                }}
              >
                <HomeIcon />
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>
      </>
    );
  }

  // ========== MAP PAGE HEADER ==========
  if (isMapPage) {
    return (
      <>
        <AppBar
          position="absolute"
          color="transparent"
          elevation={0}
          sx={{ top: "1rem", zIndex: 1000 }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters sx={{ gap: 1.5 }}>
              {/* Menu Burger */}
              <Tooltip title="Menu" arrow placement="bottom">
                <IconButton
                  size="large"
                  edge="start"
                  aria-label="menu"
                  onClick={toggleDrawer(true)}
                  sx={{
                    color: "white",
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    width: 48,
                    height: 48,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'white',
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    },
                    '& svg': {
                      color: 'primary.main',
                      fontSize: '1.5rem'
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>

              {/* Logo dan Judul */}
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                  color: "primary.main",
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  px: 2.5,
                  py: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'white',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  }
                }}
              >
                <Avatar
                  src={`${environment.api}site-settings/logo`}
                  alt={`logo ${siteSetting?.name || "SIBATNAS"}`}
                  sx={{
                    width: 36,
                    height: 36,
                    mr: 1.5,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                />
                <Typography
                  variant="h6"
                  noWrap
                  component="h2"
                  sx={{
                    fontFamily: "finalsix, sans-serif",
                    fontWeight: 700,
                    display: { xs: "none", sm: "block" },
                    fontSize: '1.1rem',
                    letterSpacing: '0.5px'
                  }}
                >
                  {siteSetting?.name || "SIBATNAS"}
                </Typography>
              </Box>

              {/* Spacer */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Layer Menu Button */}
              <Tooltip title="Layer Peta" arrow placement="bottom">
                <IconButton
                  size="large"
                  aria-label="layer menu"
                  onClick={handleLayerMenuClick}
                  sx={{
                    color: "primary.main",
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    width: 48,
                    height: 48,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'white',
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    }
                  }}
                >
                  <Badge
                    badgeContent={mapLayer.length}
                    color="primary"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.7rem',
                        height: 18,
                        minWidth: 18,
                        fontWeight: 700
                      }
                    }}
                  >
                    <LayersIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* Login Button */}
              <Tooltip title={isLoggedIn ? user?.username || "Akun" : "Login"} arrow placement="bottom">
                <IconButton
                  size="large"
                  aria-label="login"
                  onClick={(e) => setLoginMenuAnchorEl(e.currentTarget)}
                  sx={{
                    color: "primary.main",
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    width: 48,
                    height: 48,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'white',
                      transform: 'scale(1.05)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    }
                  }}
                >
                  <AccountCircle />
                </IconButton>
              </Tooltip>
            </Toolbar>
          </Container>
        </AppBar>

        {/* Layer Menu */}
        <Menu
          anchorEl={layerMenuAnchorEl}
          open={layerMenuOpen}
          onClose={handleLayerMenuClose}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 380,
              maxWidth: 420,
              maxHeight: 520,
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              overflow: 'hidden',
            },
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          {/* Layer Menu Header */}
          <Box sx={{
            p: 2.5,
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}>
            <Box sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <LayersIcon sx={{ fontSize: '1.5rem' }} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'white' }}>
                Layer Peta
              </Typography>
              <Typography variant="caption" sx={{ opacity: 1, color: 'rgba(255,255,255,0.95)' }}>
                Kelola layer yang aktif
              </Typography>
            </Box>
            <Chip
              label={mapLayer.length}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.25)',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.85rem',
                height: 28,
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
          </Box>

          {mapLayer.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: alpha('#1976d2', 0.08),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <LayersIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.5 }} />
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
                Belum ada layer
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Tambahkan layer dari katalog untuk mulai menjelajah
              </Typography>
              <Button
                variant="contained"
                startIcon={<BookIcon />}
                onClick={() => {
                  openCatalog();
                  handleLayerMenuClose();
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  boxShadow: '0 2px 8px rgba(25,118,210,0.3)'
                }}
              >
                Buka Katalog
              </Button>
            </Box>
          ) : (
            <>
              <List dense sx={{ py: 1.5, maxHeight: 340, overflowY: 'auto' }}>
                {mapLayer.map((layer, index) => (
                  <Box key={`${layer.id}-${index}`}>
                    <ListItem
                      sx={{
                        px: 2,
                        py: 1.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha('#1976d2', 0.04),
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 44 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleLayerVisibility(layer.id)}
                          sx={{
                            bgcolor: layer.visible !== false ? alpha('#1976d2', 0.1) : alpha('#000', 0.05),
                            color: layer.visible !== false ? 'primary.main' : 'grey.400',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: layer.visible !== false ? alpha('#1976d2', 0.2) : alpha('#000', 0.1),
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          {layer.visible !== false ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                        </IconButton>
                      </ListItemIcon>
                      <ListItemText
                        primary={layer.title || `Layer ${index + 1}`}
                        secondary={layer.description || 'Tidak ada deskripsi'}
                        primaryTypographyProps={{
                          variant: "body2",
                          sx: {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '180px',
                            fontWeight: layer.visible !== false ? 600 : 500,
                            color: layer.visible !== false ? 'text.primary' : 'text.disabled'
                          }
                        }}
                        secondaryTypographyProps={{
                          variant: "caption",
                          sx: {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '180px',
                            color: layer.visible !== false ? 'text.secondary' : 'text.disabled'
                          }
                        }}
                      />
                      <Tooltip title="Zoom ke Layer" arrow>
                        <IconButton
                          size="small"
                          onClick={() => handleZoomToLayer(layer.id)}
                          sx={{
                            ml: 0.5,
                            color: 'primary.main',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: alpha('#1976d2', 0.1),
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <ZoomInIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteLayer(layer.id)}
                        sx={{
                          ml: 0.5,
                          color: 'error.main',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: alpha('#d32f2f', 0.1),
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                    {/* Opacity Slider */}
                    <Box sx={{ px: 2, pb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <OpacityIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Slider
                          value={layer.opacity !== undefined ? layer.opacity : 1}
                          min={0}
                          max={1}
                          step={0.1}
                          onChange={(e, value) => handleLayerOpacity(layer.id, value)}
                          sx={{
                            flex: 1,
                            '& .MuiSlider-thumb': {
                              width: 16,
                              height: 16,
                            }
                          }}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                        />
                        <Typography variant="caption" sx={{ minWidth: 35, textAlign: 'right', color: 'text.secondary' }}>
                          {Math.round((layer.opacity !== undefined ? layer.opacity : 1) * 100)}%
                        </Typography>
                      </Box>
                    </Box>
                    {index < mapLayer.length - 1 && <Divider sx={{ mx: 2 }} />}
                  </Box>
                ))}
              </List>

              <Divider />
              <Box sx={{ p: 1.5 }}>
                <MenuItem
                  onClick={() => {
                    openCatalog();
                    handleLayerMenuClose();
                  }}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    color: 'primary.main',
                    fontWeight: 600,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha('#1976d2', 0.08),
                      transform: 'translateX(4px)'
                    }
                  }}
                >
                  <ListItemIcon>
                    <BookIcon color="primary" />
                  </ListItemIcon>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Tambah Layer dari Katalog
                  </Typography>
                  <ChevronRightIcon sx={{ ml: 'auto', fontSize: '1.2rem' }} />
                </MenuItem>
              </Box>
            </>
          )}
        </Menu>

        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
          PaperProps={{
            sx: {
              boxShadow: '4px 0 24px rgba(0,0,0,0.12)'
            }
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Login Modal */}
        <LoginModal
          anchorEl={loginMenuAnchorEl}
          open={loginMenuOpen}
          onClose={() => setLoginMenuAnchorEl(null)}
        />
      </>
    );
  }

  // ========== STANDARD PAGE HEADER ==========
  const allMenuItems = menuCategories.flatMap(cat => cat.items);

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'white',
          color: 'primary.main',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ py: 1.5 }}>
            {/* Logo dan Judul */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                color: "primary.main",
                mr: 4,
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                }
              }}
            >
              <Avatar
                src={`${environment.api}site-settings/logo`}
                alt={`logo ${siteSetting?.name || "SIBATNAS"}`}
                sx={{
                  width: 48,
                  height: 48,
                  mr: 2,
                  bgcolor: 'primary.light',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              />
              <Box>
                <Typography
                  variant="h6"
                  noWrap
                  component="h1"
                  sx={{
                    fontFamily: "finalsix, sans-serif",
                    fontWeight: 800,
                    display: { xs: "none", sm: "block" },
                    lineHeight: 1.2
                  }}
                >
                  {siteSetting?.name || "SIBATNAS"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: { xs: "none", sm: "block" },
                    color: 'text.secondary',
                    fontSize: '0.7rem'
                  }}
                >
                  Sistem Informasi Batimetri Nasional
                </Typography>
              </Box>
            </Box>

            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Menu Items untuk desktop */}
            <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
              {allMenuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Button
                    key={item.text}
                    component={RouterLink}
                    to={item.path}
                    startIcon={item.icon}
                    sx={{
                      color: isActive ? 'white' : 'text.primary',
                      textTransform: 'none',
                      px: 2.5,
                      py: 1,
                      borderRadius: 2.5,
                      bgcolor: isActive ? item.color : 'transparent',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.9rem',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      boxShadow: isActive ? `0 2px 8px ${alpha(item.color, 0.3)}` : 'none',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: isActive ? '80%' : '0%',
                        height: '2px',
                        bgcolor: isActive ? 'white' : item.color,
                        transition: 'width 0.2s ease',
                      },
                      '&:hover': {
                        bgcolor: isActive ? item.color : alpha(item.color, 0.08),
                        color: isActive ? 'white' : item.color,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 4px 12px ${alpha(item.color, 0.2)}`,
                        '&::before': {
                          width: '80%',
                        }
                      }
                    }}
                  >
                    {item.text}
                  </Button>
                );
              })}
            </Box>

            {/* Menu burger untuk mobile */}
            <IconButton
              size="large"
              edge="start"
              aria-label="menu"
              onClick={toggleDrawer(true)}
              sx={{
                display: { xs: "block", md: "none" },
                color: 'primary.main',
                ml: 2,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'primary.light',
                  transform: 'rotate(90deg)',
                }
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* Login Button */}
            <Tooltip title={isLoggedIn ? user?.username || "Akun" : "Login"} arrow>
              <IconButton
                size="large"
                aria-label="login"
                onClick={(e) => setLoginMenuAnchorEl(e.currentTarget)}
                sx={{
                  ml: 2,
                  color: 'primary.main',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: 'primary.light',
                    transform: 'scale(1.1)',
                  }
                }}
              >
                <AccountCircle />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            boxShadow: '4px 0 24px rgba(0,0,0,0.12)'
          }
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Login Modal */}
      <LoginModal
        anchorEl={loginMenuAnchorEl}
        open={loginMenuOpen}
        onClose={() => setLoginMenuAnchorEl(null)}
      />
    </>
  );
}

export default Header;